import React, { PropTypes } from "react";
import {
	ControlLabel,
	FormControl,
	FormGroup,
	HelpBlock,
} from "react-bootstrap";
import VirtualizedSelect from "react-virtualized-select";
import { FormattedMessage, intlShape } from "react-intl";
import _ from "lodash";

class SelectInput extends React.Component {
	constructor(props, context) {
		super(props, context);

		this.state = {
			sortedOptions: [],
			needsResize: true,
			selectWidth: 0,
		};
	}

	componentWillMount() {
		this.updateSelectInput(this.props);
	}

	componentDidMount() {
		this._updateSelectWidth();
	}

	componentWillReceiveProps(nextProps) {
		if (!_.isEqual(nextProps.options, this.props.options)) {
			this.setState({
				needsResize: true,
			});
		}
		this.updateSelectInput(nextProps);
	}

	componentDidUpdate() {
		this._updateSelectWidth();
	}

	updateSelectInput(props) {
		const {
			addBlankOption,
			blankMessageKey,
			blankOptionValue,
			noOptionsAction,
			options,
			sortOptions,
			reverseSortOptions,
		} = props;

		const formattedOptions = options.map((option) => {
			const text =
				option.text ||
				this.context.intl.formatMessage(
					{ id: option.key },
					option.placeHolders
				);
			return {
				value: `${option.value}`,
				selected: option.value === props.input.value,
				text,
			};
		});

		let sortedOptions = sortOptions
		? [...formattedOptions].sort((a, b) => a.text.trim().localeCompare(b.text.trim()))
		: formattedOptions;

		if (reverseSortOptions) {
			sortedOptions = sortedOptions.reverse();
		}

		const blankKey = blankMessageKey || "common.blank";

		if (addBlankOption && blankMessageKey) {
			addBlankOption &&
				sortedOptions.unshift({
					value: blankOptionValue,
					text: this.context.intl.formatMessage({ id: blankKey }),
				});
		}

		this.setState({ sortedOptions });

		const emptyLength = addBlankOption ? 1 : 0;
		if (sortedOptions.length === emptyLength && noOptionsAction) {
			noOptionsAction();
		}
	}

	_updateSelectWidth() {
		if (this.state.needsResize && this.state.sortedOptions.length) {
			const selectSizer = document.createElement("select");
			const largestInput = _.maxBy(
				this.state.sortedOptions,
				(option) => option.text.length
			);
			const option = document.createElement("option");
			option.innerText = !!largestInput ? largestInput.text : " ";
			selectSizer.appendChild(option);

			selectSizer.style.position = "absolute";
			selectSizer.style.top = "-99999px";
			selectSizer.style.boxSizing = "border-box";

			document.body.appendChild(selectSizer);

			this.setState({
				needsResize: false,
				selectWidth: selectSizer.clientWidth,
			});

			document.body.removeChild(selectSizer);
		}
	}

	render() {
		const {
			addBlankOption,
			disabled,
			groupClassName,
			id,
			input,
			label,
			loading,
			meta: { touched, error, warning },
			multi,
			onChange,
			onFocus,
			onBlur,
			options,
			required,
			optionRenderer,
			componentClassName,
			asyncSearch,
			optionalProps,
			customPlaceHolder,
			clearable,
		} = this.props;

		let extraProps = {};

		if(optionalProps) {
			extraProps = optionalProps;
		}

		const scene = this.context.scene ? "-" + this.context.scene : "";
		let validationState = touched && !!error ? error.state || "error" : null;
		let validationMessageKey =
			!!error && error.messageKey ? error.messageKey : null;
		let validationValues = !!error && error.values ? error.values : {};

		if (!!warning && touched) {
			/**
				* Allows us to display error on field, but also make the request to the api. With the error option, no api call is made.
				*/
			validationState = warning.state;
			validationMessageKey = warning.messageKey;
			validationValues = warning.values ? warning.values : {};
		}

		let cleanValue = input.value;
		if (
			(addBlankOption && input.value === null) ||
			input.value === undefined ||
			input.value === ""
		) {
			input.value = null;
			cleanValue = " ";
		}
		
		const processedInput = { ...input, value: `${cleanValue}` };

		const placeholder = this.context.intl.formatMessage({
			id: !customPlaceHolder ? "common.select" : customPlaceHolder,
		});
		const searchable = this.props.searchable || false;

		return (
			<FormGroup
				className={groupClassName}
				controlId={id + scene}
				validationState={validationState}
			>
				{!!label && (
					<ControlLabel>
						{label} {required ? <span className="required"> *</span> : ""}
					</ControlLabel>
				)}
				<div className="input-wrapper">
					<FormControl
						{...processedInput}
						componentClass={VirtualizedSelect}
						className={componentClassName}
						multi={multi}
						disabled={(options.length === 0 && !asyncSearch)|| disabled || loading}
						onFocus={(e) => {
							onFocus && onFocus(e);
							return input.onFocus(e);
						}}
						onChange={(selection) => {
							let value;
							if(multi){
								value=selection ? selection : [];
							}else{
								value = selection ? selection.value : null;
							}							

							const emulatedEvent = {
								target: {
									value,
									name: input.name,
								},
							};
							onChange && onChange(emulatedEvent);

							if (multi) {
								return input.onChange(value.length ? value.map((v) => v.value).join(",") : null);
							}
							return input.onChange(value);
						}}
						onBlur={() => {
							onBlur && onBlur(input.value);
							return input.onBlur(input.value);
						}}
						autosize={true}
						searchable={searchable}
						onInputChange={this.props.asyncSearch}
						resetValue={{ value: " " }}
						onCloseResetsInput={!asyncSearch}
						onBlurResetsInput={!asyncSearch}
						clearable={clearable !== undefined ? clearable : searchable}
						labelKey="text"
						valueKey="value"
						placeholder={placeholder}
						autoBlur={true}
						options={this.state.sortedOptions}
						optionRenderer={optionRenderer}
						{...extraProps}
						style={{ minWidth: this.state.selectWidth }}
					/>
					{((touched && error) || (touched && warning)) && (
						<HelpBlock>
							<FormattedMessage
								id={validationMessageKey}
								values={validationValues}
							/>
						</HelpBlock>
					)}
				</div>
			</FormGroup>
		);
	}
}

SelectInput.propTypes = {
	clearable: PropTypes.bool,
	disabled: PropTypes.bool,
	groupClassName: PropTypes.string,
	id: PropTypes.string.isRequired,
	input: PropTypes.object.isRequired,
	label: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
	meta: PropTypes.object.isRequired,
	multi: PropTypes.bool,
	noOptionsAction: PropTypes.func,
	options: PropTypes.array.isRequired,
	optionRenderer: PropTypes.func,
	sortOptions: PropTypes.bool,
	reverseSortOptions: PropTypes.bool,
	required: PropTypes.bool,
	validation: PropTypes.object,
	validationKey: PropTypes.string,
	onChange: PropTypes.func,
	onFocus: PropTypes.func,
	onBlur: PropTypes.func,
	blankMessageKey: PropTypes.string,
	addBlankOption: PropTypes.bool,
	blankOptionValue: PropTypes.string,
	loading: PropTypes.bool,
	asyncSearch: PropTypes.func,
	optionalProps: PropTypes.object,
	searchable: PropTypes.bool,
	componentClassName: PropTypes.string,
	customPlaceHolder: PropTypes.string,
	defaultSelectValue: PropTypes.string,
	updateDefaultValueInStore: PropTypes.func
};

SelectInput.defaultProps = {
	blankOptionValue: " ",
	loading: false,
	multi: false,
	sortOptions: true,
	reverseSortOptions: false,
	clearable: undefined,
};

SelectInput.contextTypes = {
	scene: PropTypes.string,
	intl: intlShape,
};

export default SelectInput;



// WEBPACK FOOTER //
// ./src/components/common/form/SelectInput.js